import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface BeginnerProgressContextType {
  currentStep: number;
  totalSteps: number;
  currentPath: 'A' | 'B' | null;
  setStep: (step: number) => void;
  setPath: (path: 'A' | 'B' | null) => void;
  markStepComplete: (step: number) => void;
  resetProgress: () => void;
  isStepComplete: (step: number) => boolean;
}

const BeginnerProgressContext = createContext<BeginnerProgressContextType | undefined>(undefined);

const STORAGE_KEY = 'beginner_progress_state';

export const BeginnerProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<number>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.currentStep || 1;
      } catch {
        return 1;
      }
    }
    return 1;
  });

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return new Set(parsed.completedSteps || []);
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  const [currentPath, setCurrentPath] = useState<'A' | 'B' | null>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.currentPath || null;
      } catch {
        return null;
      }
    }
    return null;
  });

  const totalSteps = 5; // 3 wizard questions + 1 stock selection + 1 contract selection

  // Persist to localStorage whenever state changes
  useEffect(() => {
    const state = {
      currentStep,
      completedSteps: Array.from(completedSteps),
      currentPath
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [currentStep, completedSteps, currentPath]);

  const setStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  const setPath = (path: 'A' | 'B' | null) => {
    setCurrentPath(path);
  };

  const markStepComplete = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCompletedSteps(prev => new Set(prev).add(step));
      // Auto-advance to next step if not already past it
      if (currentStep === step && step < totalSteps) {
        setCurrentStep(step + 1);
      }
    }
  };

  const isStepComplete = (step: number): boolean => {
    return completedSteps.has(step);
  };

  const resetProgress = () => {
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setCurrentPath(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <BeginnerProgressContext.Provider
      value={{
        currentStep,
        totalSteps,
        currentPath,
        setStep,
        setPath,
        markStepComplete,
        resetProgress,
        isStepComplete
      }}
    >
      {children}
    </BeginnerProgressContext.Provider>
  );
};

export const useBeginnerProgress = () => {
  const context = useContext(BeginnerProgressContext);
  if (context === undefined) {
    throw new Error('useBeginnerProgress must be used within a BeginnerProgressProvider');
  }
  return context;
};
